import { useConfirmTotpEnrollment } from "@babackapp/private-api-client/client/use-confirm-totp-enrollment";
import { useCurrentUser } from "@babackapp/private-api-client/client/use-current-user";
import { useDisableMfa } from "@babackapp/private-api-client/client/use-disable-mfa";
import { useRegenerateMfaRecoveryCodes } from "@babackapp/private-api-client/client/use-regenerate-mfa-recovery-codes";
import { useStartTotpEnrollment } from "@babackapp/private-api-client/client/use-start-totp-enrollment";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { type FormEvent, useState } from "react";
import Loading from "@/components-legacy/loading.tsx";
import MfaRecoveryCodes from "./mfa-recovery-codes";

const TOTP_CODE_PATTERN = /^\d{6}$/;

function getErrorMessage(error: unknown): string | null {
	if (
		error &&
		typeof error === "object" &&
		"message" in error &&
		typeof error.message === "string"
	) {
		return error.message;
	}
	return null;
}

function ErrorMessage({ error }: { error: unknown }) {
	const message = getErrorMessage(error);
	return message ? <p className="text-sm text-destructive">{message}</p> : null;
}

interface RecoveryCodesState {
	codes: string[];
	isAfterEnrollment: boolean;
}

export default function MfaSettings() {
	const t = useTranslation();
	const { data: user, isLoading } = useCurrentUser();
	const [recoveryCodes, setRecoveryCodes] =
		useState<RecoveryCodesState | null>(null);

	if (isLoading || !user) {
		return <Loading className="w-4 h-4" />;
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center gap-2">
				<p className="text-sm text-muted-foreground">
					{t("accountSettings.mfa.description")}
				</p>
				{user.mfaEnabled && (
					<Badge variant="success-outline">{t("accountSettings.mfa.enabled")}</Badge>
				)}
			</div>

			{recoveryCodes ? (
				<MfaRecoveryCodes
					codes={recoveryCodes.codes}
					isAfterEnrollment={recoveryCodes.isAfterEnrollment}
					onDone={() => setRecoveryCodes(null)}
				/>
			) : user.mfaEnabled ? (
				<MfaEnabledActions
					onRecoveryCodes={(codes) =>
						setRecoveryCodes({ codes, isAfterEnrollment: false })
					}
				/>
			) : (
				<MfaEnrollment
					onEnabled={(codes) =>
						setRecoveryCodes({ codes, isAfterEnrollment: true })
					}
				/>
			)}
		</div>
	);
}

function MfaEnrollment({
	onEnabled,
}: {
	onEnabled: (recoveryCodes: string[]) => void;
}) {
	const t = useTranslation();
	const [isStarted, setIsStarted] = useState(false);
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	const start = useStartTotpEnrollment();
	const confirm = useConfirmTotpEnrollment();
	const enrollment = start.data;

	const cancel = () => {
		setIsStarted(false);
		setPassword("");
		setCode("");
		start.reset();
		confirm.reset();
	};

	if (!isStarted) {
		return (
			<Button className="w-fit" onClick={() => setIsStarted(true)}>
				{t("accountSettings.mfa.enable")}
			</Button>
		);
	}

	if (!enrollment) {
		const handleStart = (event: FormEvent) => {
			event.preventDefault();
			start.mutate({ password });
		};

		return (
			<form onSubmit={handleStart} className="flex flex-col gap-3">
				<Label htmlFor="mfa-password">
					{t("accountSettings.mfa.passwordPrompt")}
				</Label>
				<Input
					id="mfa-password"
					type="password"
					value={password}
					onChange={(event) => setPassword(event.target.value)}
					autoComplete="current-password"
					autoFocus
				/>
				<ErrorMessage error={start.error} />
				<div className="flex gap-2">
					<Button type="submit" disabled={!password || start.isPending}>
						{start.isPending ? <Loading className="w-4 h-4" /> : t("misc.continue")}
					</Button>
					<Button type="button" variant="ghost" onClick={cancel}>
						{t("misc.cancel")}
					</Button>
				</div>
			</form>
		);
	}

	const handleConfirm = async (event: FormEvent) => {
		event.preventDefault();
		const { recoveryCodes } = await confirm.mutateAsync({ code });
		onEnabled(recoveryCodes);
	};

	return (
		<form
			onSubmit={(event) => void handleConfirm(event).catch(() => setCode(""))}
			className="flex flex-col gap-3"
		>
			<p className="text-sm">{t("accountSettings.mfa.scan")}</p>
			<img
				src={enrollment.qrCodeDataUrl}
				alt=""
				className="h-44 w-44 rounded border"
			/>
			<p className="text-xs text-muted-foreground">
				{t("accountSettings.mfa.manualKey")}{" "}
				<code className="font-mono break-all">
					{enrollment.secret.match(/.{1,4}/g)?.join(" ")}
				</code>
			</p>
			<Label htmlFor="mfa-confirm-code">{t("accountSettings.mfa.code")}</Label>
			<Input
				id="mfa-confirm-code"
				value={code}
				onChange={(event) =>
					setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
				}
				inputMode="numeric"
				autoComplete="one-time-code"
				className="w-40"
				autoFocus
			/>
			<ErrorMessage error={confirm.error} />
			<div className="flex gap-2">
				<Button
					type="submit"
					disabled={!TOTP_CODE_PATTERN.test(code) || confirm.isPending}
				>
					{confirm.isPending ? (
						<Loading className="w-4 h-4" />
					) : (
						t("accountSettings.mfa.activate")
					)}
				</Button>
				<Button type="button" variant="ghost" onClick={cancel}>
					{t("misc.cancel")}
				</Button>
			</div>
		</form>
	);
}

type EnabledAction = "regenerate" | "disable";

function MfaEnabledActions({
	onRecoveryCodes,
}: {
	onRecoveryCodes: (recoveryCodes: string[]) => void;
}) {
	const t = useTranslation();
	const [action, setAction] = useState<EnabledAction | null>(null);
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	const regenerate = useRegenerateMfaRecoveryCodes();
	const disable = useDisableMfa();

	const close = () => {
		setAction(null);
		setPassword("");
		setCode("");
		regenerate.reset();
		disable.reset();
	};

	if (!action) {
		return (
			<div className="flex gap-2">
				<Button variant="outline" onClick={() => setAction("regenerate")}>
					{t("accountSettings.mfa.regenerate")}
				</Button>
				<Button variant="outline" onClick={() => setAction("disable")}>
					{t("accountSettings.mfa.disable")}
				</Button>
			</div>
		);
	}

	const mutation = action === "regenerate" ? regenerate : disable;

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		if (action === "regenerate") {
			const { recoveryCodes } = await regenerate.mutateAsync({ code });
			close();
			onRecoveryCodes(recoveryCodes);
			return;
		}

		const trimmedCode = code.trim();
		await disable.mutateAsync(
			TOTP_CODE_PATTERN.test(trimmedCode)
				? { password, code: trimmedCode }
				: { password, recoveryCode: trimmedCode },
		);
		close();
	};

	return (
		<form
			onSubmit={(event) => void handleSubmit(event).catch(() => setCode(""))}
			className="flex flex-col gap-3"
		>
			<p className="text-sm">
				{action === "regenerate"
					? t("accountSettings.mfa.regenerateDescription")
					: t("accountSettings.mfa.disableDescription")}
			</p>
			{action === "disable" && (
				<>
					<Label htmlFor="mfa-disable-password">
						{t("accountSettings.currentPassword")}
					</Label>
					<Input
						id="mfa-disable-password"
						type="password"
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						autoComplete="current-password"
						autoFocus
					/>
				</>
			)}
			<Label htmlFor="mfa-action-code">
				{action === "regenerate"
					? t("accountSettings.mfa.code")
					: t("accountSettings.mfa.codeOrRecoveryCode")}
			</Label>
			<Input
				id="mfa-action-code"
				value={code}
				onChange={(event) => setCode(event.target.value)}
				inputMode={action === "regenerate" ? "numeric" : "text"}
				autoComplete="one-time-code"
				className="w-56"
				autoFocus={action === "regenerate"}
			/>
			<ErrorMessage error={mutation.error} />
			<div className="flex gap-2">
				<Button
					type="submit"
					variant={action === "disable" ? "destructive" : "default"}
					disabled={
						!code.trim() ||
						(action === "disable" && !password) ||
						mutation.isPending
					}
				>
					{mutation.isPending ? <Loading className="w-4 h-4" /> : t("misc.confirm")}
				</Button>
				<Button type="button" variant="ghost" onClick={close}>
					{t("misc.cancel")}
				</Button>
			</div>
		</form>
	);
}
